import {
  ConsolePage,
  NewFeatureModal,
  useModalActions,
} from '@carboncopyco/components';
import {
  selectShowtimeDomain,
  useLazyGetDemoQuery,
  useLazyGetPresentationQuery,
} from '@carboncopyco/data';
import type { IDemo, Playbook, TopAssetType } from '@carboncopyco/types';
import { PresentationTypeConst } from '@carboncopyco/types/desktop';
import { notify } from '@carboncopyco/ui';
import {
  AnalyticsTrackEventName,
  PlayEventNames,
  useAnalytics,
} from '@carboncopyco/utils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SHOULD_SHOW_AFTER_LOGIN_MODAL_KEY } from '../../../shared/constants/constants';
import GiveADemoModalExtended from '../../components/GiveADemoModalExtended';
import useAsyncMemo from '../../hooks/useAsyncMemo';
import { useCustomNavigate } from '../../hooks/useCustomNavigate';
import { usePlaybookPresentations } from '../../hooks/usePlaybookPresentations';
import { useWorkspacesMap } from '../../hooks/useWorkspacesMap';
import { useAppService } from '../../services/AppService';
import { usePresentationService } from '../../services/presenter/PresentationService';
import { useWorkspaces } from '../../store/context';
import { SidebarContentType } from '../../types/WorkspacesTypes';

export const ConsoleScreen = () => {
  const {
    navigateToDemoPage,
    navigateToRecorderFromHomepage,
    navigateToTourCapture,
    navigateToCreateOverlay,
    navigateToWebTourCapture,
    navigateToTourPreview,
    navigateToActivities,
    links,
    navigateToNotificationsSettings,
    navigateToDashboard,
    navigateToWebIntegrationSettings,
  } = useCustomNavigate();
  const flags = useFlags();
  const { trackEvent } = useAnalytics();
  const trackPlayNow = (assetId: number, isDemo?: boolean) =>
    trackEvent(PlayEventNames.PLAY_CLICK_PLAY_NOW, {
      [isDemo ? 'demoId' : 'playbookId']: assetId,
      location: 'homepage.give_a_demo_modal',
    });
  const trackPlayLater = (assetId: number, isDemo?: boolean) => {
    trackEvent(PlayEventNames.PLAY_CLICK_PLAY_LATER, {
      [isDemo ? 'demoId' : 'playbookId']: assetId,
      location: 'homepage.give_a_demo_modal',
    });
  };
  const workspacesMap = useWorkspacesMap();
  const { switchWorkspace, setActiveSidebarType } = useWorkspaces();
  const { startPreviewDemo } = useAppService();
  const { modalActionsByName } = useModalActions();
  const giveADemoModal = modalActionsByName('give-a-demo-modal');
  const [selectedDemo, setSelectedAsset] = useState<IDemo | null>(null);
  const [selectedPlaybook, setSelectedPlaybook] = useState<Playbook | null>(
    null
  );
  const { organizationId = '' } = useParams();
  const { data: showModal, isLoading } = useAsyncMemo<
    Record<any, any>
  >(async () => {
    const data = await window.dsDesktop?.me();
    return data[SHOULD_SHOW_AFTER_LOGIN_MODAL_KEY] ?? false;
  }, []);

  const handleDemoImageClickNOS = async (demo: IDemo) => {
    const ws = workspacesMap[demo.workspaceId];
    if (!ws) return;
    switchWorkspace(ws);
    navigateToDemoPage(String(demo?.id), ws.id);
  };

  const goToDemoPreview = async (demo: IDemo) => {
    trackEvent(AnalyticsTrackEventName.DEMO_CLICK_PREVIEW, {
      location: 'homepage',
      demoId: demo.id,
    });

    await handleDemoImageClickNOS(demo);

    startPreviewDemo(demo);
  };

  const { handlePlayPlaybook } = usePlaybookPresentations({
    organizationId,
  });

  const { openDemoPresentation } = usePresentationService();
  const [getDemo] = useLazyGetDemoQuery();
  const [getPresentation] = useLazyGetPresentationQuery();

  const navigate = useNavigate();
  const goToAssetPage = (
    {
      id,
      type,
      workspaceId,
    }: {
      id: string;
      type: TopAssetType;
      workspaceId: string;
    },
    options?: { tab?: string }
  ) => {
    let link: string;
    switch (type) {
      case 'demo':
        link = links.getDemoPageLinkByWorkspace(
          Number(id),
          workspaceId,
          options?.tab
        );
        break;
      case 'tour':
        link = links.getTourPageLinkByWorkspace(
          Number(id),
          workspaceId,
          options?.tab
        );
        break;
      case 'playbook':
        link = links.getPlaybookPageLinkByWorkspace(
          id,
          workspaceId,
          options?.tab
        );
        break;
    }
    switchWorkspace(workspacesMap[workspaceId]);
    navigate(link);
  };

  return (
    <div className="flex flex-col justify-between items-center w-full bg-ds-natural-50 h-full overflow-y-auto">
      <ConsolePage
        previewDemo={goToDemoPreview}
        previewTour={async (tourId, workspaceId) => {
          switchWorkspace(
            workspacesMap[workspaceId],
            SidebarContentType.Workspace
          );
          navigateToTourPreview(tourId);
          navigate(
            links.getTourPageLinkByWorkspace(Number(tourId), workspaceId)
          );
        }}
        previewPlaybook={(playbookId: string, workspaceId: string) => {
          switchWorkspace(workspacesMap[workspaceId]);
          navigate(
            `${links.getPlaybookPageLinkByWorkspace(playbookId, workspaceId)}?mode=preview`
          );
        }}
        goToAssetPage={goToAssetPage}
        openGiveADemoModal={(demo?: IDemo) => {
          if (demo) {
            setSelectedAsset(demo);
            giveADemoModal.openModal();
          }
        }}
        openGiveAPlaybookModal={(playbook: Playbook) => {
          setSelectedPlaybook(playbook);
          giveADemoModal.openModal();
        }}
        handlePlay={(play) => {
          if (play.asset.assetType === 'playbook') {
            handlePlayPlaybook({
              playSubset: play,
            });
            return;
          }

          const playPresentation = play.presentations[0];

          const analyticsSessionId = window.dsDesktop.analyticsClient.start({
            presentationId: String(playPresentation.presentationId),
            presentationType: PresentationTypeConst.demo,
            resourceType: 'managed_presentation',
            forClient: play.forClient,
          });

          getPresentation({
            id: play.presentations[0].presentationId,
            organizationId,
          })
            .unwrap()
            .then((presentation) => {
              return getDemo({
                workspaceId: play.workspaceId,
                id: play.asset.assetId,
                organizationId,
              })
                .unwrap()
                .then((demo) => {
                  return openDemoPresentation(
                    presentation,
                    demo,
                    analyticsSessionId
                  );
                })
                .catch(() => {
                  notify.error('Something went wrong');
                });
            })
            .catch(() => {
              notify.error('Something went wrong');
            });
        }}
        goToRecorder={navigateToRecorderFromHomepage}
        createTourFromLiveApp={navigateToWebTourCapture}
        createTourFromDemo={(id) => navigateToTourCapture({ demoId: id })}
        createOverlay={navigateToCreateOverlay}
        goToTeamActivity={() => {
          setActiveSidebarType(SidebarContentType.None);
          navigateToActivities();
        }}
        goToNotificationSettings={() => {
          setActiveSidebarType(SidebarContentType.None);
          navigateToNotificationsSettings();
        }}
        goToAnalyticsDashboard={() => {
          navigateToDashboard();
        }}
        goToIntegrationSettings={() => {
          setActiveSidebarType(SidebarContentType.None);
          navigateToWebIntegrationSettings();
        }}
      />

      {giveADemoModal.isModalOpen() && selectedDemo && (
        <GiveADemoModalExtended
          open={giveADemoModal.isModalOpen()}
          entity={selectedDemo}
          onPresent={() => {
            trackPlayNow(Number(selectedDemo.id));
            giveADemoModal.closeModal();
          }}
          onSave={() => {
            trackPlayLater(Number(selectedDemo.id));
            giveADemoModal.closeModal();
            notify.success('Demo presentation saved successfully');
          }}
          onClose={giveADemoModal.closeModal}
          workspaceId={selectedDemo.workspaceId}
        />
      )}
      {showModal && !isLoading && flags.newFeatureModal && (
        <NewFeatureModal
          open={!isLoading && !!showModal}
          handleClose={() => {
            window.dsDesktop.user.setShouldShowAgainAfterLoginModal(false);
          }}
        />
      )}

      {giveADemoModal.isModalOpen() && selectedPlaybook && (
        <GiveADemoModalExtended
          workspaceId={selectedPlaybook.workspaceId}
          open={giveADemoModal.isModalOpen()}
          entity={selectedPlaybook}
          onPresent={() => {
            trackPlayNow(Number(selectedPlaybook.id));
            giveADemoModal.closeModal();
          }}
          onSave={() => {
            trackPlayLater(Number(selectedPlaybook?.id));
            giveADemoModal.closeModal();
            notify.success('Playbook presentation saved successfully');
          }}
          onClose={giveADemoModal.closeModal}
        />
      )}
    </div>
  );
};
